import { useState } from 'react';
import {
  Button,
  Footnote,
  FormGroup,
  FormRow,
  Group,
  Notice,
  Row,
  SectionLabel,
  StatusPill,
} from '@store/ui';
import { api } from '../api';
import { LegalLinks, openExternal, SUPPORT_HANDLE, SUPPORT_URL } from '../legal';
import { haptic } from '../telegram';
import { useAsync } from './shared';

// --- Support ----------------------------------------------------------------

/**
 * The store-support sentence, in Russian, as the copy rather than the fallback.
 *
 * The server answers in whatever locale it resolved for the session, and when
 * it resolves wrong it puts an English sentence in the middle of a Russian
 * screen — which is exactly what shipped. So this is the default, and the
 * server's version is only taken when it is demonstrably in the same language
 * as everything around it. A wrong locale now costs a less specific sentence
 * instead of an unreadable one.
 */
const SUPPORT_NOTE =
  'Поддержку по покупкам оказывает магазин. Опишите, что случилось, — оператор ответит в этом чате.';

const CYRILLIC = /[а-яё]/i;

export function SupportScreen() {
  const tickets = useAsync(() => api.tickets(), []);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sent, setSent] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const serverNote = tickets.data?.notice;
  const note = serverNote && CYRILLIC.test(serverNote) ? serverNote : SUPPORT_NOTE;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      const result = await api.createTicket({ subject, body });
      setSent(result.message);
      setSubject('');
      setBody('');
      tickets.reload();
      haptic.success();
    } catch {
      haptic.error();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container stack">
      <h1 className="title">Поддержка</h1>

      {/* A confirmation of something the customer just did is the one thing on
          this screen that has earned a box — it has to be noticed, and a tick
          rather than an alarm mark says which kind of news it is. */}
      {sent ? <Notice icon="check">{sent}</Notice> : null}

      {/*
       * An iOS grouped form: the group is the surface, the two fields are rows
       * inside it separated by a hairline, and the note about who answers is a
       * footnote under the group.
       *
       * What was here: a bordered card wrapping two more boxes, each with a
       * label above a field of its own, the multi-line one rendered as a
       * 120px stadium because `.textarea` resolved to the capsule radius. Three
       * nested surfaces to ask two questions.
       */}
      <form className="stack stack--tight" onSubmit={submit}>
        <FormGroup>
          <FormRow label="Тема" htmlFor="support-subject">
            <input
              id="support-subject"
              className="input"
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
              placeholder="Коротко о проблеме"
              required
              minLength={3}
              maxLength={200}
            />
          </FormRow>
          <FormRow stacked label="Что случилось" htmlFor="support-body">
            <textarea
              id="support-body"
              className="textarea"
              rows={4}
              value={body}
              onChange={(event) => setBody(event.target.value)}
              placeholder="Номер заказа и что пошло не так"
              required
              minLength={3}
              maxLength={4000}
            />
          </FormRow>
        </FormGroup>

        <Footnote>{note}</Footnote>

        <Button type="submit" block loading={busy}>
          Отправить
        </Button>
      </form>

      {tickets.data?.tickets.length ? (
        <section>
          <SectionLabel>Мои обращения</SectionLabel>
          <Group>
            {tickets.data.tickets.map((ticket) => (
              <Row
                key={ticket.id}
                title={ticket.subject}
                subtitle={`#${ticket.number}`}
                trailing={<StatusPill>{ticket.statusLabel}</StatusPill>}
              />
            ))}
          </Group>
        </section>
      ) : null}

      {/*
       * The way out of this screen when the form is not the answer.
       *
       * A ticket is asynchronous and lives inside the shop; a customer whose
       * order is already wrong wants a person. This contact is the one thing on
       * the screen that is never absent — `SUPPORT_URL` falls back to the
       * store's own account precisely because an unreachable support link is a
       * worse failure than an unpublished document.
       */}
      <section>
        <SectionLabel>Связаться напрямую</SectionLabel>
        <Group>
          <Row
            icon="bubble"
            title={SUPPORT_HANDLE ?? 'Написать в Telegram'}
            subtitle="Ответим в личных сообщениях"
            onClick={() => {
              haptic.select();
              openExternal(SUPPORT_URL);
            }}
          />
        </Group>
      </section>

      {/* The documents, when the owner has published them. Same component and
          same words as the home screen and the checkout, so the customer meets
          one set of links rather than three. */}
      <footer>
        <LegalLinks />
      </footer>
    </div>
  );
}
